import type ApplicationInstance from '@ember/application/instance';
import { on } from '@ember/modifier';
import { getOwner } from '@ember/owner';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { ValidatorMap } from 'ember-changeset/types';
import { task } from 'ember-concurrency';
import perform from 'ember-concurrency/helpers/perform';
import type { IntlService } from 'ember-intl';
import type ApplicationAdapter from 'parog-web/adapters/application';
import Content from 'parog-web/components/consultation/report-form/content';
import FileAttachmentDisplayer from 'parog-web/components/file-attachment-displayer';
import type { ForcBaseArgs } from 'parog-web/components/forc/abstract/base-component';
import JournalEventBaseComponent from 'parog-web/components/forc/abstract/journal-event-based-component';
import Provider from 'parog-web/components/forc/provider';
import JournalEventRetrievalStrategies from 'parog-web/components/forc/utils/journal-event-retrieval-strategies';
import ConsultationReportWrittenJournalEvent from 'parog-web/models/consultation-report-written-journal-event';
import type ConsultationReportWrittenQuestion from 'parog-web/models/consultation/consultation-report-written-question';
import tWithDefault from 'parog-web/utils/t-with-default';

interface ConsultationReportFormComponentArgs extends ForcBaseArgs {
  consultationType: string;
  shouldResetDraft: boolean;
  isDisplayedFromPanel?: boolean;
}

export default class ConsultationReportFormComponent extends JournalEventBaseComponent<ConsultationReportFormComponentArgs> {
  static override forcDisplayName = 'Compte rendu de consultation';
  static override fullComponentId = 'consultation/report-form';
  static override journalEventClass = ConsultationReportWrittenJournalEvent;

  @service declare intl: IntlService;

  @tracked currentQuestions: ConsultationReportWrittenQuestion[] = [];
  @tracked declare isConsultationReportDirty: boolean;

  override applicationAdapter = (getOwner(this) as ApplicationInstance).lookup(
    'adapter:application'
  ) as ApplicationAdapter;
  override journalEventProperties = [
    'occurredAt',
    'consultationType',
    'actionsCarriedOut',
    'questions',
    'fileAttachmentsAttributes'
  ];
  override retrieveJournalEvent =
    JournalEventRetrievalStrategies.sourceJournalEvent;
  override shouldAutoSaveDraft = true;

  override get changeset() {
    const changeset = super.changeset;
    if (this.args.shouldResetDraft && this.draft) {
      this.draft.clear();
    }
    return changeset;
  }

  get consultationLabel(): string {
    const consultationType = this.changeset.get('consultationType');
    return tWithDefault(
      this.intl,
      `consultation-history.${consultationType}`,
      consultationType
    );
  }

  override get isDirty(): boolean {
    return this.isConsultationReportDirty ? true : super.isDirty;
  }

  get isFromDashboard(): boolean {
    const consultationType = this.changeset.get('consultationType');
    return !!this.intl.getTranslation(
      `consultation-history.${consultationType}`,
      'fr-fr'
    );
  }

  get questions(): ConsultationReportWrittenQuestion[] {
    // We only provide the questions that are in relation with
    // the current consultationType
    const currentQuestionsInChangeset = this.changeset.get('questions');

    return this.currentQuestions?.map(currentQuestion => {
      // If the question exists in the changeset (i.e. in the draft)
      // We provide it
      const alreadyExistingQuestionInChangeset =
        currentQuestionsInChangeset.find(q => q.key == currentQuestion.key);
      if (alreadyExistingQuestionInChangeset) {
        return alreadyExistingQuestionInChangeset;
      }
      // Otherwise, we provide the default question
      return currentQuestion;
    });
  }

  override get validations(): ValidatorMap {
    return this.journalEventClass.validations(this.intl);
  }

  generatePdf = task(async (_e: Event) => {
    fetch(
      `/api/v1/consultation-report-written-journal-events/${this.sourceJeId}.pdf`,
      {
        method: 'GET',
        headers: Object.assign(
          {
            'Content-Type': 'application/json'
          },
          this.applicationAdapter.headers
        )
      }
    )
      .then(response => {
        if (response.ok) {
          return response.blob();
        }
        throw new Error('Téléchargement échoué');
      })
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `Compte rendu ${this.changeset.consultationType} ${this.patient.fullName}`;
        a.click();
        window.URL.revokeObjectURL(url);

        this.flashMessages.success('Le PDF a été téléchargé avec succès.', {
          title: 'Téléchargement terminé'
        });
      })
      .catch(() => {
        this.flashMessages.warning(
          'Une erreur est survenue lors du téléchargement.',
          {
            title: 'Erreur'
          }
        );
      });
  });

  override retrieveModel(): Record<string, unknown> {
    const model = super.retrieveModel();
    if (this.isNewMode) {
      model.questions = ConsultationReportWrittenJournalEvent.getQuestions(
        this.args.consultationType
      );
      model.occurredAt = new Date();
      model.consultationType = this.args.consultationType;
    }

    return model;
  }

  // As we store every questions, even when changing consultation type
  // We just want the ones we are currently editing
  override async save(): Promise<ConsultationReportWrittenJournalEvent> {
    const questionsChangeset = this.changeset.get('questions');
    const curatedQuestionsChangeset = questionsChangeset.filter(question =>
      this.currentQuestions.map(q => q.key).includes(question.key)
    );
    this.changeset.set('questions', curatedQuestionsChangeset);

    return await super.save();
  }

  setCurrentQuestions = (
    questions: ConsultationReportWrittenQuestion[]
  ): void => {
    this.currentQuestions = questions;
  };

  updateQuestion = (): void => {
    this.isConsultationReportDirty = true;
  };

  <template>
    <Provider @isHeaderHidden={{@isDisplayedFromPanel}} @forcComponent={{this}}>
      <:content as |Form|>
        {{#unless @isDisplayedFromPanel}}
          <div class='ui header !flex justify-between [&_i]:cursor-pointer'>
            <h2>Compte rendu
              {{this.consultationLabel}}
              de
              {{@patient.fullName}}</h2>
            <i
              role='button'
              class='icon download semeia primary-color'
              {{on 'click' (perform this.generatePdf)}}
            ></i>
          </div>
        {{/unless}}
        <Content
          @forcComponent={{this}}
          @form={{Form}}
          @isDisplayedFromPanel={{@isDisplayedFromPanel}}
          @onUpdateQuestion={{this.updateQuestion}}
          @patient={{@patient}}
          @setCurrentQuestions={{this.setCurrentQuestions}}
          @questions={{this.questions}}
          @shouldHideConsultationType={{this.isFromDashboard}}
        />

        <Form.FileUpload @propertyName='fileAttachmentsAttributes' />
        {{#if this.retrievedJournalEvent.fileAttachments}}
          {{#each
            this.retrievedJournalEvent.fileAttachments
            as |fileAttachment|
          }}
            <FileAttachmentDisplayer
              @shouldDisplayDownloadLink={{true}}
              @fileAttachment={{fileAttachment}}
              @alternativeText='pièce jointe du compte rendu de consultation'
            />
          {{/each}}
        {{/if}}
      </:content>
      <:footer as |Footer|>
        {{#unless @isDisplayedFromPanel}}
          <Footer.CancelButton />
        {{/unless}}
        <Footer.SubmitButton />
      </:footer>
    </Provider>
  </template>
}
